import React from 'react';
import { Box, Typography, Button, Container ,Grid, IconButton} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import './offers.scss';
import Heading from '../../Components/heading.tsx';
import SupervisorHeading from '../../../WebApp/Components/Heading/heading.tsx';

interface Product {
  id: string;
  name: string;
  price: number;
  quantity?: number;
  image: string;
  originalPrice?: number;
  discountPercent?: number;
}
interface CartScreenProps {
  cartItems: Product[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  onDelete: (id: string) => void;
  onBack: () => void;
  onPlaceOrder: () => void;
  placeOrderLabel?: string; 
  userRole?: string; 
}

const CartScreen: React.FC<CartScreenProps> = ({
  cartItems,
  onAdd,
  onRemove,
  onDelete,
  onBack,
  onPlaceOrder,
  placeOrderLabel = "Place Order",
  userRole,
}) => {
  const totalQuantity = cartItems.reduce((acc, item) => acc + (item.quantity || 1), 0);
  const totalActualCost = cartItems.reduce(
    (acc, item) => acc + (item.originalPrice || item.price) * (item.quantity || 1),
    0
  );
  const totalPayable = cartItems.reduce(
    (acc, item) => acc + item.price * (item.quantity || 1),
    0
  );

const isVendorSupervisor = ["vendor_supervisor", "vendor_company_admin", "client_company_admin"].includes(userRole);
if (isVendorSupervisor) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        m: 0,
        p: 0,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          position: "sticky",
          top: 10,
          zIndex: 1100,
        }}
      >
        <SupervisorHeading
          title="My Cart"
          closeIcon="false"
          backArrow={true}
          onBack={onBack}
          className="no-margin-heading"
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          pt: 1,
          pb: "calc(56px + 20px)", 
          background: "#f8fafb",
          minHeight: 0,
        }}
      >
        <Box sx={{ maxWidth: "100%", mx: "auto", width: "100%" }}>
          {cartItems.length === 0 ? (
            <Box className="empty-cart">
              <Typography className="empty-cart-text">
                Your cart is empty.
              </Typography>
              <button className="start-shopping-btn" onClick={onBack}>
                Start Shopping
              </button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {cartItems.map((item) => (
                <Grid item key={item.id} xs={12} sm={12} md={6} lg={4}>
                  <div className="vendor-supervisor">
                    <Box className="cart-item">
                      <Box className="cart-item-image-wrapper">
                        {item.discountPercent! > 0 && (
                          <Box className="discount-badge">
                            {item.discountPercent}% off
                          </Box>
                        )}
                        <img
                          src={item.image}
                          alt={item.name}
                          className="cart-item-image"
                        />
                      </Box>

                      <Box className="cart-item-info">
                        <Typography className="cart-item-name">
                          {item.name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography className="product-price">
                            ₹{item.price}
                          </Typography>
                          {item.originalPrice &&
                            item.originalPrice > item.price && (
                              <Typography className="product-original-price">
                                ₹{item.originalPrice}
                              </Typography>
                            )}
                        </Box>
                        <Box className="cart-item-quantity-row">
                          <Box className="cart-item-controls">
                            <button
                              className="qty-btn"
                              onClick={() => onRemove(item.id)}
                            >
                              -
                            </button>
                            <span className="cart-item-qty">
                              {item.quantity || 1}
                            </span>
                            <button
                              className="qty-btn"
                              onClick={() => onAdd(item.id)}
                            >
                              +
                            </button>
                          </Box>
                          <button
                            className="cart-item-delete-icon-inline"
                            onClick={() => onDelete(item.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box
                  sx={{
                    background: "#fff",
                    p: 2,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: 420,
                    mx: "auto",
                    gap: 1,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <Box
                    className="cart-summary-top"
                    sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
                  >
                    <Box display="flex" alignItems="center" flexWrap="wrap">
                      <Typography
                        component="span"
                        sx={{ fontWeight: 600, fontSize: "20px" }}
                      >
                        Total&nbsp;
                      </Typography>
                      <Typography component="span">
                        ({totalQuantity} item
                        {totalQuantity !== 1 ? "s" : ""})
                      </Typography>
                      <Typography component="span" sx={{ mx: "6px" }}>
                        :
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: "20px",
                          color: "#222",
                        }}
                      >
                        ₹{totalPayable}
                      </Typography>
                      {totalActualCost > totalPayable && (
                        <Typography
                          className="product-original-price"
                          sx={{ ml: "8px" }}
                        >
                          ₹{totalActualCost}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    className="start-shopping-btn"
                    onClick={onPlaceOrder}
                  >
                    {placeOrderLabel}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
}

  return (
    <Container
      maxWidth={isVendorSupervisor ? "lg" : "sm"} 
      sx={{
        paddingX: 2,
        paddingBottom: "90px",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 100,
          pt: "30px",
          pb: "8px",
        }}
      >
        <Heading title="My Cart" onBackClick={onBack} />
      </Box>

      <Box
        className="cart-content"
        sx={{
          display: isVendorSupervisor ? "grid" : "block",  
          gridTemplateColumns: isVendorSupervisor ? "2fr 1fr" : "1fr", 
          gap: isVendorSupervisor ? 3 : 0,
        }}
      >
        <Box>
          {cartItems.length === 0 ? (
            <Box className="empty-cart">
              <Typography variant="body1" className="empty-cart-text">
                <strong>Your cart is empty.</strong>
              </Typography>
              <Button className="start-shopping-btn" onClick={onBack}>
                Start Shopping
              </Button>
            </Box>
          ) : (
            <>
              {cartItems.map((item) => (
                <Box key={item.id} className="cart-item">
                  <Box className="cart-item-image-wrapper">
                    {typeof item.discountPercent === "number" &&
                      item.discountPercent > 0 && (
                        <Box className="discount-badge">
                          {item.discountPercent}% off
                        </Box>
                      )}
                    <img
                      src={item.image}
                      alt={item.name}
                      className="cart-item-image"
                    />
                  </Box>
                  <Box className="cart-item-info">
                    <Typography className="cart-item-name">
                      {item.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography className="cart-item-price">
                        ₹{item.price}
                      </Typography>
                      {item.originalPrice &&
                        item.originalPrice > item.price && (
                          <Typography className="cart-item-original-price">
                            ₹{item.originalPrice}
                          </Typography>
                        )}
                    </Box>
                    <Box className="cart-item-quantity-row">
                      <Box className="cart-item-controls">
                        <button
                          className="qty-btn"
                          onClick={() => onRemove(item.id)}
                        >
                          -
                        </button>
                        <span className="cart-item-qty">
                          {item.quantity || 1}
                        </span>
                        <button
                          className="qty-btn"
                          onClick={() => onAdd(item.id)}
                        >
                          +
                        </button>
                      </Box>
                      <button
                        className="cart-item-delete-icon-inline"
                        onClick={() => onDelete(item.id)}
                        aria-label="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </Box>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>

        {cartItems.length > 0 && (
          <Box>
            <Box
              className="cart-summary-top"
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <Box display="flex" alignItems="center" flexWrap="wrap">
                <Typography component="span" style={{ fontWeight: 600 }}>
                  Total&nbsp;
                </Typography>
                <Typography component="span">
                  ({totalQuantity} item{totalQuantity !== 1 ? "s" : ""})
                </Typography>
                <Typography component="span" style={{ margin: "0 6px" }}>
                  :
                </Typography>
                <Typography
                  component="span"
                  style={{
                    fontWeight: 700,
                    fontSize: "17px",
                    color: "#222",
                  }}
                >
                  ₹{totalPayable}
                </Typography>
                {totalActualCost > totalPayable && (
                  <Typography
                    className="product-original-price"
                    style={{ marginLeft: "8px" }}
                  >
                    ₹{totalActualCost}
                  </Typography>
                )}
              </Box>
            </Box>

            <Button
              className="checkout-btn"
              fullWidth
              onClick={onPlaceOrder}
            >
              {placeOrderLabel}
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default CartScreen;
